import './styles/reviewsTable.scss';
import 'primeicons/primeicons.css';

import React, {ChangeEventHandler, MouseEventHandler, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {DataTable, DataTablePageEvent, DataTableRowClickEvent, DataTableStateEvent} from 'primereact/datatable';
import {Column, ColumnBodyOptions} from 'primereact/column';
import {ColumnGroup} from 'primereact/columngroup';
import {Row} from 'primereact/row';
import {InputText} from 'primereact/inputtext';
import BolRating from '../../bolRating/bolRating';
import {AccessKey, ReviewTableProduct} from '../../../util/interfaces';
import {AwesomeIcon} from '@dvrd/dvr-controls';
import classNames from 'classnames';
import useRight from '../../misc/useRight';
import IDate from '@dvrd/idate';
import InfoContainer from '../../misc/infoContainer';

interface Props {
    onSort: (evt: DataTableStateEvent) => void;
    onChangeSearch: ChangeEventHandler<HTMLInputElement>;
    onChangePage: (evt: DataTablePageEvent) => void;
    onClickRow: (evt: DataTableRowClickEvent) => void;
    onClickCell: (ean: string, type: 'positive' | 'negative') => MouseEventHandler;
    products: Array<ReviewTableProduct>;
    total: number;
    firstRow: number;
    loading: boolean;
    sortField: string;
    sortOrder: 0 | 1 | -1 | null | undefined;
    search: string;
    limit: number;
    fromDate: IDate;
    toDate: IDate;
}

export default function ReviewsTable(props: Props) {
    const {t} = useTranslation();
    const [canView] = useRight(AccessKey.RATING_VIEW);
    const {
        products,
        loading,
        onSort,
        limit,
        sortField,
        sortOrder,
        firstRow,
        search,
        total,
        onChangePage,
        onChangeSearch,
        onClickRow,
        onClickCell,
        fromDate,
        toDate
    } = props;
    const periodLabel = useMemo(() => {
        const days = toDate.absDiff(fromDate, 'days');
        return `(${t('reviewAnalytics.periodLabel.last', 'Afgelopen').toLowerCase()} ${days} ${
            days === 1 ? t('reviewAnalytics.periodLabel.day', 'dag') : t('reviewAnalytics.periodLabel.days', 'dagen')
        })`;
    }, [fromDate, toDate, t]);

    function renderImage(product: ReviewTableProduct) {
        return <img className='image-container' src={product.image_url} alt='' />;
    }

    function renderRating(product: ReviewTableProduct) {
        const score = product.score_rating;
        return (
            <div className='rating-cell'>
                {score} <BolRating value={product.score_rating} />
            </div>
        );
    }

    function renderNPS(npsType: 'total' | 'period') {
        return function (product: ReviewTableProduct) {
            let value: number | string = npsType === 'total' ? product.nps_score : product.period_nps_score;
            if ((npsType === 'total' && !product.total_reviews) || (npsType === 'period' && !product.period_total))
                value = '-';
            else value = `${value}`;
            return <span>{value}</span>;
        };
    }

    function renderTitle(product: ReviewTableProduct) {
        return (
            <>
                <span title={product.title}>
                    {product.title}
                    <br />
                </span>
                <span className='ean-style'>EAN: {product.ean}</span>
            </>
        );
    }

    function renderHeader() {
        return (
            <div className='table-header'>
                <span className='p-input-icon-left'>
                    <i className='pi pi-search' />
                    <InputText
                        value={search}
                        onChange={onChangeSearch}
                        placeholder={t('reviewAnalytics.reviewsTable.search', 'Zoeken')}
                    />
                </span>
            </div>
        );
    }

    function renderClickableCell(product: ReviewTableProduct, options: ColumnBodyOptions) {
        const {field} = options;
        const value = product[field as keyof ReviewTableProduct];
        const withArrows = ['period_negative', 'period_positive'].includes(field) && value !== 0;
        let clickType: 'positive' | 'negative';
        let caretColor: string;
        if (field.endsWith('positive')) {
            clickType = 'positive';
            caretColor = 'green';
        } else {
            clickType = 'negative';
            caretColor = 'red';
        }
        return (
            <div
                className={classNames('clickable-cell', !canView && 'cursor-default')}
                onClick={canView ? onClickCell(product.ean, clickType) : undefined}
            >
                <span>
                    {withArrows && (
                        <>
                            <AwesomeIcon name='caret-up' className={classNames('caret-icon', caretColor)} />
                            &nbsp;
                        </>
                    )}
                    {value || '-'}
                </span>
                {canView && <AwesomeIcon name='arrow-up-right-from-square' className='link-icon' />}
            </div>
        );
    }

    function renderDashCell(product: ReviewTableProduct, options: ColumnBodyOptions) {
        const value = product[options.field as keyof ReviewTableProduct];
        return (
            <div className={classNames('clickable-cell', !canView && 'cursor-default')}>
                <span>{value || '-'}</span>
            </div>
        );
    }

    function renderNPSHeader(infoPosition: 'left' | 'right') {
        return function () {
            return (
                <div className={classNames('nps-header', infoPosition)}>
                    <label>NPS</label>
                    <InfoContainer className='nps-info'>
                        {t(
                            'reviewAnalytics.nps.info',
                            'De NPS (net promoter score) geeft aan hoe waarschijnlijk jouw klanten het product dat ze kochten aan zouden raden aan anderen. Deze score wordt berekend door het percentage positieve reviews (5 sterren) te verminderen met het percentage negatieve reviews (1, 2 of 3 sterren). Deze score loopt van -100 t/m 100, waarbij -100 aangeeft dat er alleen maar negatieve reviews zijn en 100 aangeeft dat er alleen maar positieve reviews zijn.'
                        )}
                    </InfoContainer>
                </div>
            );
        };
    }

    function renderHeaderGroup() {
        return (
            <ColumnGroup>
                <Row>
                    <Column header='' colSpan={2} />
                    <Column header={t('reviewAnalytics.reviewsTable.totalReviews', 'Totaal reviews')} colSpan={4} />
                    <Column
                        header={`${t('reviewAnalytics.reviewsTable.periodReviews', 'Periode reviews')} ${periodLabel}`}
                        colSpan={4}
                    />
                    <Column header={t('reviewAnalytics.reviewsTable.score', 'Score')} colSpan={1} />
                </Row>
                <Row>
                    <Column
                        field='image_url'
                        header=''
                        body={renderImage}
                        style={{padding: '0 .5rem', width: 'unset'}}
                    />
                    <Column
                        field='title'
                        sortable
                        header={t('reviewAnalytics.reviewsTable.name', 'Naam')}
                        body={renderTitle}
                        style={{maxWidth: '15rem'}}
                    />
                    <Column field='total' sortable header={t('reviewAnalytics.reviewsTable.total', 'Totaal')} />
                    <Column
                        field='total_positive'
                        sortable
                        header={t('reviewAnalytics.reviewsTable.positive', 'Positief')}
                    />
                    <Column
                        field='total_negative'
                        sortable
                        header={t('reviewAnalytics.reviewsTable.negative', 'Negatief')}
                    />
                    <Column field='nps_score' sortable header={renderNPSHeader('right')} />
                    <Column field='period_total' sortable header={t('reviewAnalytics.reviewsTable.total', 'Totaal')} />
                    <Column
                        field='period_positive'
                        sortable
                        header={t('reviewAnalytics.reviewsTable.positive', 'Positief')}
                    />
                    <Column
                        field='period_negative'
                        sortable
                        header={t('reviewAnalytics.reviewsTable.negative', 'Negatief')}
                    />
                    <Column field='period_nps_score' sortable header={renderNPSHeader('left')} />
                    <Column field='rating' sortable header={t('reviewAnalytics.reviewsTable.rating', 'Rating')} />
                </Row>
            </ColumnGroup>
        );
    }

    return (
        <div className='reviews-table-container'>
            <DataTable
                value={products}
                lazy
                onPage={onChangePage}
                headerColumnGroup={renderHeaderGroup()}
                totalRecords={total}
                header={renderHeader()}
                paginator
                rows={limit}
                loading={loading}
                globalFilterFields={['name']}
                showGridlines
                rowsPerPageOptions={[1, 5, 10, 25, 50]}
                first={firstRow}
                onRowClick={canView ? onClickRow : undefined}
                selectionMode='single'
                onSort={onSort}
                className={classNames('reviews-table', !canView && 'cursor-default')}
                sortField={sortField}
                sortOrder={sortOrder}
                defaultSortOrder={-1}
                emptyMessage={t(
                    'reviewAnalytics.reviewsTable.emptyMessage',
                    'Data aan het verzamelen, kom morgen terug...'
                )}
                scrollable
            >
                <Column field='image_url' header='' body={renderImage} style={{padding: '0 .5rem', width: 'unset'}} />
                <Column
                    field='title'
                    header={t('reviewAnalytics.reviewsTable.name', 'Naam')}
                    body={renderTitle}
                    style={{maxWidth: '15rem', minWidth: '15rem'}}
                />
                <Column
                    field='total_reviews'
                    header={t('reviewAnalytics.reviewsTable.total', 'Totaal')}
                    body={renderDashCell}
                />
                <Column
                    field='total_positive'
                    header={t('reviewAnalytics.reviewsTable.positive', 'Positief')}
                    style={{padding: 0}}
                    body={renderClickableCell}
                />
                <Column
                    field='total_negative'
                    header={t('reviewAnalytics.reviewsTable.negative', 'Negatief')}
                    style={{padding: 0}}
                    body={renderClickableCell}
                />
                <Column field='nps_score' header='NPS' body={renderNPS('total')} />
                <Column
                    field='period_total'
                    header={t('reviewAnalytics.reviewsTable.period', 'Periode')}
                    body={renderDashCell}
                />
                <Column
                    field='period_positive'
                    header={t('reviewAnalytics.reviewsTable.positive', 'Positief')}
                    style={{padding: 0}}
                    body={renderClickableCell}
                />
                <Column
                    field='period_negative'
                    header={t('reviewAnalytics.reviewsTable.negative', 'Negatief')}
                    style={{padding: 0}}
                    body={renderClickableCell}
                />
                <Column field='period_nps_score' header='NPS' body={renderNPS('period')} />
                <Column
                    field='score_rating'
                    header={t('reviewAnalytics.reviewsTable.rating', 'Rating')}
                    body={renderRating}
                />
            </DataTable>
        </div>
    );
}
